import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from 'react'

const AccountEditContext = createContext()

export const AccountEditProvider = ({children}) => {
    const [isEditing, setIsEditing] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [isDiscardModalOpen, setDiscardModalOpen] = useState(false)

    const isDirtyRef = useRef(false)
    const pendingActionRef = useRef(null)

    const startEditing = useCallback(() => setIsEditing(true), [])

    const stopEditing = useCallback(() => {
        isDirtyRef.current = false
        pendingActionRef.current = null
        setIsEditing(false)
        setIsDirty(false)
        setDiscardModalOpen(false)
    }, [])

    const setFormDirty = useCallback((dirty) => {
        isDirtyRef.current = dirty
        setIsDirty(dirty)
    }, [])

    const requestDiscard = useCallback((action) => {
        if (!isDirtyRef.current) {
            return true
        }
        pendingActionRef.current = action
        setDiscardModalOpen(true)
        return false
    }, [])

    const cancelDiscard = useCallback(() => {
        pendingActionRef.current = null
        setDiscardModalOpen(false)
    }, [])

    const discardChanges = useCallback(() => {
        const action = pendingActionRef.current
        pendingActionRef.current = null
        stopEditing()
        
        setTimeout(() => action?.(), 0)
    }, [stopEditing])

    const value = useMemo(
        () => ({
            isEditing,
            isDirty,
            startEditing,
            stopEditing,
            setFormDirty,
            isDiscardModalOpen,
            requestDiscard,
            cancelDiscard,
            discardChanges
        }),
        [
            isEditing,
            isDirty,
            startEditing,
            stopEditing,
            setFormDirty,
            isDiscardModalOpen,
            requestDiscard,
            cancelDiscard,
            discardChanges
        ]
    )

    return <AccountEditContext.Provider value={value}>{children}</AccountEditContext.Provider>
}

export const useAccountEdit = () => useContext(AccountEditContext)
